import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import Keycloak from 'keycloak-js'

interface AuthUser {
  id: string
  username: string
  email: string
  roles: string[]
}

interface AuthConfig {
  authority: string
  clientId: string
  redirectUri: string
  postLogoutRedirectUri: string
}

interface AuthContextType {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
  login: () => void
  logout: () => void
  hasRole: (role: string) => boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

let keycloakInstance: Keycloak | null = null

async function fetchAuthConfig(): Promise<AuthConfig> {
  const response = await fetch('/api/auth/config')
  if (!response.ok) {
    throw new Error('Failed to fetch auth config')
  }
  return response.json()
}

function extractUserFromToken(keycloak: Keycloak): AuthUser | null {
  if (!keycloak.tokenParsed) return null

  const parsed = keycloak.tokenParsed as {
    sub?: string
    preferred_username?: string
    email?: string
    realm_access?: { roles?: string[] }
  }

  return {
    id: parsed.sub || '',
    username: parsed.preferred_username || '',
    email: parsed.email || '',
    roles: parsed.realm_access?.roles || [],
  }
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true

    async function initAuth() {
      try {
        const config = await fetchAuthConfig()

        const keycloakUrl = config.authority.replace(/\/realms\/.*$/, '')
        const keycloakRealm = config.authority.split('/realms/')[1]
        const keycloak = new Keycloak({
          url: keycloakUrl,
          realm: keycloakRealm,
          clientId: config.clientId,
        })

        keycloakInstance = keycloak

        keycloak.onTokenExpired = () => {
          keycloak.updateToken(30).catch(() => {
            keycloak.login()
          })
        }

        keycloak.onAuthSuccess = () => {
          if (mounted) {
            setUser(extractUserFromToken(keycloak))
            setToken(keycloak.token || null)
          }
        }

        keycloak.onAuthRefreshSuccess = () => {
          if (mounted) {
            setToken(keycloak.token || null)
          }
        }

        const authenticated = await keycloak.init({
          onLoad: 'login-required',
          checkLoginIframe: false,
        })

        if (mounted) {
          if (authenticated) {
            setUser(extractUserFromToken(keycloak))
            setToken(keycloak.token || null)
          }
          setIsLoading(false)
        }
      } catch (error) {
        console.error('Auth initialization failed:', error)
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    initAuth()

    return () => {
      mounted = false
    }
  }, [])

  const login = useCallback(() => {
    keycloakInstance?.login()
  }, [])

  const logout = useCallback(() => {
    keycloakInstance?.logout()
  }, [])

  const hasRole = useCallback(
    (role: string) => {
      return user?.roles.includes(role) || false
    },
    [user]
  )

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    token,
    login,
    logout,
    hasRole,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function getAuthToken(): string | null {
  return keycloakInstance?.token || null
}
